import { Button, Space, Tabs } from '@mantine/core';
import { useContext, useEffect, useState } from 'react';
import { axios } from '../../../../utils/axios';
import { useParams } from 'react-router-dom';
import { AppLoader } from '../../../../components/common/loader';
import { ViewBom } from './viewBom';
import { Col, Row } from 'react-bootstrap';
import ContentPaper from '../../../../components/layouts/rfs/Content';
import { ProjectOverview } from '../projectOverview';
import { CostingRequestsContext, useCrStatusCancelled } from '../../../../contexts/CostingRequest';
import {
  filterCommonSkus,
  filterUniqueSkus,
  getPlants,
  isDualRefSku,
  makePayload,
  previewOnly,
} from './helper';
import { showAlert } from '../../../../utils/alerts';
import { NpsComponent } from '../../../../common/nps';
import { NpsIds, notificationPopup } from '../../../../constants';
import { NotificationModal } from '../../../../components/modal/NotificationModal';
import Single from './layouts/single';
import Dual from './layouts/dual';

export const ReferenceSKU = ({ activeTab }) => {
  const isCancelled = useCrStatusCancelled();
  const params = useParams();
  const { crNumber } = useParams();
  const { info, isCrRoleAuthorized } = useContext(CostingRequestsContext);
  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [bomModal, setBomsModal] = useState({
    open: false,
    sku: null,
    plant: null,
  });
  const [selectedSkus, setSelectSkus] = useState({});
  const [selectedSkusPayload, setSkusPayload] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [infoLoading, setInfoLoading] = useState(false);
  const [activePlant, setActivePlant] = useState();
  const [readOnly, setReadOnly] = useState(false);
  const [referenceSkus, setReferenceSkus] = useState([]);
  const [npsOpened, setNpsOpened] = useState(false);
  const { producing_plant_data = [], sku_data = [] } = info;
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryLoader, setCountryLoader] = useState(false);
  const [recommendedSkus, setRecommendedSkus] = useState([]); //required for country change to hold original value
  const [requestedSku, setRequestedSku] = useState({});
  const [packagingRefSku, setPackagingSku] = useState({});
  const [brewingRefSku, setBrewingRefSku] = useState({});
  const [dualRefSelections, setDualRefSelections] = useState({});
  const [savedSkus, setSavedSkus] = useState([]);

  useEffect(() => {
    if (activeTab === 'referenceSKUSelection' && sku_data.length && producing_plant_data.length) {
      fetchCountries();
      initializeSkuInfo(producing_plant_data);
      fetchSavedSkus(true, producing_plant_data[0]?.id);
      setActivePlant(producing_plant_data[0]?.id);
    }

    return () => {
      setSelectSkus({});
      setDualRefSelections({});
    };
  }, [activeTab, producing_plant_data, sku_data]);

  useEffect(() => {
    if (savedSkus.length > 0) handleSavedSkus(savedSkus, activePlant);
  }, [activePlant]);

  const initializeSkuInfo = () => {
    if (isDualRefSku(producing_plant_data[0]?.type_of_ref_sku)) {
      setBrewingRefSku(getPlants(producing_plant_data));
      setPackagingSku(getPlants(producing_plant_data));
    } else {
      setSkusPayload(getPlants(producing_plant_data));
    }
  };

  const fetchCountries = async () => {
    try {
      const { data } = await axios.get('/costing-request/distinct-countries');
      const countries = (data.data || []).filter((country) => country.PlantPrefix);
      setCountries(
        countries.map((item) => {
          return {
            label: item.Country,
            value: item.PlantPrefix,
          };
        }),
      );
    } catch (e) {
      console.log(e);
    }
  };

  const fetchSavedSkus = async (loading = true, activePlantId) => {
    try {
      setInfoLoading(loading);
      const { data } = await axios.get(
        `/costing-request/step/selected-ref-sku-selection/${params.crNumber}`,
      );

      if (data && data.length > 0) {
        setSavedSkus(data);
        handleSavedSkus(data, activePlantId);
        setReadOnly(data.length > 0);
      } else {
        fetch();
      }
    } catch (e) {
    } finally {
      setInfoLoading(false);
    }
  };

  const handleSavedSkus = (skus, activePlantId) => {
    const plantSkus = skus.filter((sku) => sku.plant_data.id === activePlantId);
    const { sku_details, secondary_sku_details } = plantSkus?.[0] || {};

    sku_details['unique'] = 'selected';
    if (secondary_sku_details) secondary_sku_details['unique'] = 'selected';

    setReferenceSkus([sku_details, ...(secondary_sku_details ? [secondary_sku_details] : [])]);
  };

  const isRefSelected = (item) => {
    return Object.keys(item?.selectedData || {}).length > 0;
  };

  const validateSelections = () => {
    const error = {};
    producing_plant_data.forEach((item) => {
      const plantId = item.id;
      if (isDualRefSku(item.type_of_ref_sku)) {
        const isBrewingValid = isRefSelected(brewingRefSku[plantId]);
        const isPackagingValid = isRefSelected(packagingRefSku[plantId]);

        if (!isBrewingValid || !isPackagingValid) error[plantId] = true;
      } else {
        const isValid = isRefSelected(selectedSkusPayload[plantId]);
        if (!isValid) error[plantId] = true;
      }
    });

    return error;
  };

  const handleSubmit = async () => {
    setNotificationModalOpen(true);
    try {
      setSubmitLoading(true);
      const validationErrors = validateSelections();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(errors);
        showAlert({ message: 'Validation failed. Please select valid Reference SKU' }, 'error');
      } else {
        const payload = makePayload(
          producing_plant_data,
          selectedSkusPayload,
          packagingRefSku,
          brewingRefSku,
        );
        await axios.post(`/costing-request/step/ref-sku-selection/${params.crNumber}`, payload);
        showAlert({ message: 'Request Submitted Successfully' }, 'success');
        fetchSavedSkus(true, activePlant);
        setNpsOpened(true);
        setReadOnly(true);
      }
    } catch (e) {
      showAlert(
        { message: e?.response?.data?.message?.Message || 'Something went wrong' },
        'error',
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const fetch = async (country = null) => {
    try {
      setLoading(true);
      if (firstLoad) await axios.get(`costing-request/step/bom-component-refresh/${crNumber}`);
      const queryParams = isDualRefSku(getReferenceSkuType()) ? { isdual: true } : {};

      const { data } = await axios.get(
        `/costing-request/step/get-ref-sku-selection/${params.crNumber}`,
        { params: queryParams },
      );
      const { StepStarted, recommended, PS, pegasus, requested } = data.data;
      setRequestedSku(requested);
      setReadOnly(!StepStarted);
      const filteredSKus = filterUniqueSkus(
        [
          ...(PS ? [{ ...PS, plant: PS.producing_plant, type: 'RFS' }] : []),
          ...(pegasus
            ? (pegasus?.combinations || []).map((sku) => {
                return { ...sku, plant: sku.producing_plant, type: 'RFS' };
              })
            : []),
        ] || [],
      );
      const filteredRecommenedSkus = filterUniqueSkus(recommended || []);
      const skus = filterCommonSkus(filteredRecommenedSkus, filteredSKus);
      const recommendedSkus = (skus.recommended || []).map((item) => ({
        ...item,
        unique: `${item.sku}_${item.plant}`,
        type: 'Recommended',
      }));
      const referenceSkusList = (skus.reference || []).map((item) => ({
        ...item,
        unique: `${item.sku}_${item.plant}`,
        type: item.type ? item.type : '',
      }));

      const finalSkus = [...recommendedSkus, ...referenceSkusList];

      setRecommendedSkus(finalSkus);
      if (!selectedCountry || country) {
        const code = country ? country : producing_plant_data[0].producing_plant.slice(0, 2);
        setSelectedCountry(code);
        fetchReferenceSkuByCountry(code, finalSkus);
      } else {
        setReferenceSkus();
      }
    } catch (e) {
      showAlert(
        { message: e?.response?.data?.message?.Message || 'Something went wrong' },
        'error',
      );
    } finally {
      setFirstLoad(false);
      setLoading(false);
    }
  };

  const fetchReferenceSkuByCountry = async (code, finalSkus = null) => {
    try {
      setCountryLoader(true);
      setSelectedCountry(code);
      const params = isDualRefSku(getReferenceSkuType()) ? { isdual: true } : {};
      const { data } = await axios.post(`/costing-request/sku-by-country/${crNumber}`, {
        ...params,
        PlantPrefix: code,
      });
      const skusList = data.data;
      const skus = filterCommonSkus(finalSkus ? finalSkus : recommendedSkus, skusList);
      const referenceSkusList = (skus.reference || []).map((item) => ({
        ...item,
        unique: `${item.sku}_${item.plant}`,
        type: item.type ? item.type : '',
      }));

      setReferenceSkus([...skus.recommended, ...referenceSkusList]);
    } catch (e) {
      showAlert(
        { message: e?.response?.data?.message?.Message || 'Something went wrong' },
        'error',
      );
    } finally {
      setCountryLoader(false);
    }
  };

  const handleRowSelection = (data, plantId, sku_id, type) => {
    if (type) {
      let currentValues = { ...dualRefSelections };
      currentValues[type] = { ...data };

      if (type === 'brewing') {
        setBrewingRefSku({
          ...brewingRefSku,
          [plantId]: {
            ...brewingRefSku[plantId],
            sku_id: sku_id,
            production_plant_id: plantId,
            selectedData: currentValues[type],
          },
        });
      } else {
        setPackagingSku({
          ...packagingRefSku,
          [plantId]: {
            ...packagingRefSku[plantId],
            sku_id: sku_id,
            production_plant_id: plantId,
            selectedData: currentValues[type],
          },
        });
      }
      setDualRefSelections(currentValues);
    } else {
      let currentValues = { ...selectedSkus };
      currentValues = { ...data };
      setSkusPayload({
        ...selectedSkusPayload,
        [plantId]: {
          ...selectedSkusPayload[plantId],
          sku_id: sku_id,
          production_plant_id: plantId,
          selectedData: currentValues,
        },
      });
      setSelectSkus(currentValues);
    }
  };

  const hasPermission = () => isCrRoleAuthorized(info.producing_plant_data);

  const isDisabled = () => {
    return (
      !hasPermission() ||
      isCancelled ||
      loading ||
      submitLoading ||
      Object.keys(validateSelections() || {}).length > 0 ||
      readOnly
    );
  };

  const getReferenceSkuType = () => {
    let type = producing_plant_data[0].type_of_ref_sku;
    if (activePlant) {
      const plant = producing_plant_data.find((plant) => plant.id === activePlant);
      type = plant.type_of_ref_sku;
    }

    return type;
  };

  const Footer = () => (
    <Row className='justify-content-md-end' md={4}>
      <Col md='auto'>
        <Button
          className='next'
          onClick={handleSubmit}
          variant='gradient'
          radius='md'
          size='md'
          fullWidth
          disabled={isDisabled()}
          style={{
            marginRight: '1rem',
            width: '200px',
          }}
        >
          {submitLoading ? <AppLoader /> : 'Submit'}
        </Button>
      </Col>
    </Row>
  );

  return (
    <>
      <ContentPaper footer={<Footer />}>
        <ProjectOverview />
        <Space h='sm' />

        <div>
          <Tabs
            keepMounted={false}
            value={activePlant}
            onTabChange={(value) => setActivePlant(value)}
            color='yellow'
          >
            <Tabs.List>
              {producing_plant_data.map(({ producing_plant, id }, index) => (
                <Tabs.Tab
                  value={id}
                  className='menu-tabs'
                  color={errors[id] ? 'red' : ''}
                  key={index}
                >
                  <span className={`${errors[id] ? 'red' : ''}`}>{producing_plant}</span>
                </Tabs.Tab>
              ))}
            </Tabs.List>

            {producing_plant_data.map(({ id, producing_country, type_of_ref_sku }, index) => {
              const sku = sku_data[0] || null;
              if (!sku) return null;

              return (
                <Tabs.Panel value={id} key={index}>
                  {isDualRefSku(type_of_ref_sku) ? (
                    <Dual
                      {...sku}
                      skuId={sku.id}
                      plantId={id}
                      skus={referenceSkus}
                      setBomsModal={setBomsModal}
                      handleRowSelection={handleRowSelection}
                      dualRefSelections={dualRefSelections}
                      readOnly={readOnly || !hasPermission() || isCancelled}
                      countries={countries}
                      fetchReferenceSkuByCountry={fetchReferenceSkuByCountry}
                      loading={loading || countryLoader || infoLoading}
                      selectedCountry={selectedCountry}
                      requestedSku={requestedSku}
                      packagingRefSku={packagingRefSku}
                      brewingRefSku={brewingRefSku}
                      previewOnly={previewOnly(savedSkus)}
                    />
                  ) : (
                    <Single
                      {...sku}
                      skuId={sku.id}
                      plantId={id}
                      skus={referenceSkus}
                      setBomsModal={setBomsModal}
                      handleRowSelection={handleRowSelection}
                      selectedSkus={selectedSkus}
                      selectedSkusDetails={selectedSkusPayload[id] || {}}
                      allSelected={selectedSkusPayload?.allSelected}
                      readOnly={readOnly || !hasPermission() || isCancelled}
                      countries={countries}
                      fetchReferenceSkuByCountry={fetchReferenceSkuByCountry}
                      loading={loading || countryLoader || infoLoading}
                      selectedCountry={selectedCountry}
                      previewOnly={previewOnly(savedSkus)}
                    />
                  )}
                </Tabs.Panel>
              );
            })}
          </Tabs>
        </div>

        {bomModal.open && (
          <ViewBom
            bomModal={bomModal}
            handleClose={() => setBomsModal({ open: false, sku: null, plant: null })}
          />
        )}
        {npsOpened && (
          <NpsComponent
            crNumber={crNumber}
            opened={npsOpened}
            setOpened={setNpsOpened}
            callback={() => {
              setNpsOpened(false);
            }}
            id={NpsIds().ref}
          />
        )}
      </ContentPaper>
      <NotificationModal
        notificationModalOpen={notificationModalOpen}
        setNotificationModalOpen={setNotificationModalOpen}
        notificationText={notificationPopup.text}
        title={notificationPopup.title}
      />
    </>
  );
};
